import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Styling, Catalogue } from '@ynap/api-utils';
import { pdpSelectors, pdpActions } from '@ynap/product-details-state';
import { productSelectors } from '@ynap/product-state';
import { raygun } from '@ynap/clientside-monitoring';
import { useConfig, useAppState } from '@ynap/immutables-provider';
import 'cross-fetch/polyfill';

const { getSelectedProductImages, getSelectedProductPrice, getProductColours } = pdpSelectors;
const { getProductLinkByPartNumber, getProductImageFromTemplate } = productSelectors;
const WIW = 'WIW_ProductRec';
const YMAL = 'YMAL_ProductRec';

const configSelector = (config) => ({
    baseUrlWCS: config?.global?.services?.commerce?.baseUrl,
    baseUrlStyling: config?.global?.services?.styling?.baseUrl,
    brandId: config?.global?.application?.brandId,
    clientIdWCS: config?.global?.services.commerce.clientId,
    clientIdStyling: config?.global?.services?.styling?.clientId,
    availableImageSizes: config?.head?.availableImageSizes,
    imageQuality: config?.head?.imageQualityPercentage,
    pdpUrlTemplate: config?.global?.urlConfig?.pdp?.construct,
    maxRecommendations: config?.head?.pdp?.maxRecommendations,
    outfitViewFirstFallback: config?.head?.pdp?.recommendations?.outfitViewFirstFallback,
    outfitViewSecondFallback: config?.head?.pdp?.recommendations?.outfitViewSecondFallback,
    imagesphereAspectRatio: config?.head?.aspectRatio?.imagesphere,
});

export const useRecommendations = ({
    imageViews = [],
    partNumber,
    recommendationsToFetch = [WIW, YMAL],
    imageTemplate,
    withProductDetails = false,
    originalOutfit = false,
}) => {
    const dispatch = useDispatch();
    const storeRecommendationsState = useSelector((state) => state.recommendations);
    const originalOutfitSegment = originalOutfit ? 'originalOutfit' : 'default';
    const storeRecommendations = storeRecommendationsState?.data?.[partNumber]?.[originalOutfitSegment];
    const { requestRecommendations, setRecommendations: setRecommendationsToStore } = pdpActions;
    const [recommendations, setRecommendations] = useState({});
    const [recommendationsWithProductDetails, setRecommendationsWithProductDetails] = useState({});
    const assertedRecommendationsToFetch = recommendationsToFetch?.filter((module) => !Object.keys(storeRecommendations || {}).includes(module));

    const shouldFetchProductDetails = withProductDetails && Object.keys(recommendations).length;

    const { country, language, langISO, locale } = useAppState();

    const {
        baseUrlWCS,
        baseUrlStyling,
        brandId,
        clientIdWCS,
        clientIdStyling,
        availableImageSizes,
        imageQuality,
        pdpUrlTemplate,
        maxRecommendations,
        outfitViewFirstFallback = 'ou',
        outfitViewSecondFallback = 'fr',
        imagesphereAspectRatio,
    } = useConfig(configSelector);

    useEffect(() => {
        const requests = getRecommendationRequests({
            partNumber,
            baseUrl: baseUrlStyling,
            brandId,
            country,
            language,
            clientId: clientIdStyling,
            locale: langISO,
            recommendationsToFetch: assertedRecommendationsToFetch,
            originalOutfit,
        });

        const fetchRecommendations = async (locale, availableImageSizes, imageQuality, pdpUrlTemplate, maxRecommendations) => {
            dispatch(requestRecommendations());
            // On change colors on wishlist, enable loading between the transition
            recommendations && setRecommendations({});
            recommendationsWithProductDetails && setRecommendationsWithProductDetails({});
            const normalizer = getNormalizer({
                availableImageSizes,
                imageQuality,
                maxRecommendations,
                pdpUrlTemplate,
                locale,
                imageTemplate,
                imageViews,
                outfitViewFirstFallback,
                outfitViewSecondFallback,
                imagesphereAspectRatio,
                recommendationsToFetch: assertedRecommendationsToFetch,
            });
            const normalizedData = await fetchData(requests, normalizer, imagesphereAspectRatio);
            dispatch(setRecommendationsToStore({ ...storeRecommendations, ...normalizedData }, partNumber, originalOutfit));
            setRecommendations({ ...storeRecommendations, ...normalizedData });
        };

        if (!storeRecommendationsState?.loading && (!storeRecommendations || assertedRecommendationsToFetch.length > 0)) {
            fetchRecommendations(locale, availableImageSizes, imageQuality, pdpUrlTemplate, maxRecommendations);
        } else if (storeRecommendations && !storeRecommendationsState?.loading && assertedRecommendationsToFetch.length === 0) {
            setRecommendations(storeRecommendations);
        }
    }, [partNumber, storeRecommendationsState?.loading]);

    useEffect(() => {
        if (shouldFetchProductDetails) {
            const recommendationName = Object.hasOwn(recommendations, YMAL) ? YMAL : WIW;
            const propsForWCSFetch = { baseUrlWCS, brandId, clientIdWCS, country, langISO };
            if (
                storeRecommendations?.[recommendationName]?.length > 0 &&
                !storeRecommendations?.[recommendationName][0]?.selectedProductVariant &&
                !storeRecommendationsState?.loading
            ) {
                dispatch(requestRecommendations());
                fetchProductDetailsFromWCS(recommendations[recommendationName], propsForWCSFetch)
                    .then((responses) => {
                        return responses.map((response) => {
                            const productDetailsFromWCS = response?.products[0];
                            const recommendationFromSS = recommendations[recommendationName].find((recommendation) => {
                                return recommendation?.partNumber && recommendation.partNumber === productDetailsFromWCS?.productColours[0]?.partNumber;
                            });

                            return addWCSProductDetailsToSSRecommendation(recommendationFromSS, productDetailsFromWCS);
                        });
                    })
                    .then((recommendationsWithSkuData) => {
                        const recommendationsWithProductDetails = { [recommendationName]: recommendationsWithSkuData };
                        dispatch(setRecommendationsToStore(recommendationsWithProductDetails, partNumber, originalOutfit));
                        setRecommendationsWithProductDetails(recommendationsWithProductDetails);
                    });
            } else if (
                (storeRecommendations?.[recommendationName]?.[0]?.selectedProductVariant && !storeRecommendationsState?.loading) ||
                storeRecommendations?.[recommendationName] === null
            ) {
                setRecommendationsWithProductDetails(storeRecommendations);
            }
        }
    }, [shouldFetchProductDetails, recommendations]);

    const items = withProductDetails ? recommendationsWithProductDetails : recommendations;
    // only return the specific recommendations requested (not everything that is cached)
    return recommendationsToFetch.reduce((acc, recommendation) => {
        acc[recommendation] = items[recommendation];
        return acc;
    }, {});
};

const fetchProductDetailsFromWCS = async (externalRecommendations, propsForWCSFetch) => {
    const { baseUrlWCS, brandId, clientIdWCS, country, langISO } = propsForWCSFetch;

    const requests = externalRecommendations?.map((recommendation) => {
        return Catalogue.getDetailsByPartNumber({
            baseUrl: baseUrlWCS,
            brandId,
            country,
            partNumber: recommendation.partNumber,
            clientId: clientIdWCS,
            options: { locale: langISO },
        });
    });

    return requests?.length ? await multipleCall(requests) : [];
};

const addWCSProductDetailsToSSRecommendation = (recommendationFromSS, productDetailsFromWCS) => {
    const { partNumber: wcsProductPartNumber } = productDetailsFromWCS;
    const { partNumber: ssProductPartNumber } = recommendationFromSS;
    const products = getProductColours(productDetailsFromWCS, wcsProductPartNumber);
    const product = products?.find(({ partNumber }) => partNumber === ssProductPartNumber);

    return {
        ...recommendationFromSS,
        ...(product && { selectedProductVariant: product }),
    };
};

// return an array of Requests of recommendation api-utils
const getRecommendationRequests = ({ partNumber, baseUrl, brandId, country, language, clientId, locale, recommendationsToFetch, originalOutfit }) => {
    const options = {
        locale,
    };

    const recommendationTypes = {
        [WIW]: 'wiw',
        [YMAL]: 'ymal',
    };

    const requestParameters = {
        baseUrl,
        brandId,
        country,
        language,
        partNumber,
        clientId,
        options,
        originalOutfit,
    };

    return recommendationsToFetch.map((recommendationsKey) => {
        return Styling.getRecommendations({
            ...requestParameters,
            recommendationsKey: recommendationTypes[recommendationsKey],
        });
    });
};

//return a function that normalize the recommendation data
const getNormalizer = ({
    availableImageSizes,
    imageQuality,
    maxRecommendations,
    pdpUrlTemplate,
    locale,
    imageTemplate,
    imageViews,
    outfitViewFirstFallback,
    outfitViewSecondFallback,
    imagesphereAspectRatio,
    recommendationsToFetch,
}) => {
    return (recommendations) => {
        return getRecommendation(
            recommendations,
            availableImageSizes,
            imageQuality,
            maxRecommendations,
            pdpUrlTemplate,
            locale,
            imageTemplate,
            imageViews,
            outfitViewFirstFallback,
            outfitViewSecondFallback,
            imagesphereAspectRatio,
            recommendationsToFetch,
        );
    };
};

//TODO extract to shared library: https://jira.nap/browse/ISPW-463
//combine requests and normalize function together and return normalized recommendation data
const fetchData = async (requests, normalize) => {
    const jsonResponses = await multipleCall(requests);
    return normalize(jsonResponses);
};

//fetch wiw and ymal
const multipleCall = async (requests) => {
    const calls = requestToPromises(requests);
    let allJson, allRes;

    try {
        allRes = await Promise.all(calls);
    } catch (error) {
        raygun &&
            raygun.helpers.agent('send', {
                error,
                tags: ['recommendations'],
            });
        // eslint-disable-next-line no-console
        return console.log('fetching error', error);
    }

    try {
        allJson = await Promise.all(allRes.map((a) => a.json()));
    } catch (error) {
        raygun &&
            raygun.helpers.agent('send', {
                error,
                tags: ['recommendations'],
            });
        // eslint-disable-next-line no-console
        return console.log('json parsing error', error);
    }
    return allJson;
};

const requestToPromises = (requests) =>
    requests.map((r) => {
        return fetch(r);
    });

const isClientError = (statusCode) => {
    const code = parseInt(statusCode);
    return Boolean(code && code >= 400 && code <= 499);
};

//normalize recommendation data
const getRecommendation = (
    recommendations,
    availableImageSizes,
    imageQuality,
    maxRecommendations,
    pdpUrlTemplate,
    locale,
    imageTemplate,
    imageViews,
    outfitViewFirstFallback,
    outfitViewSecondFallback,
    imagesphereAspectRatio,
    recommendationsToFetch,
) => {
    if (!recommendations) {
        return recommendationsToFetch.reduce((acc, value) => Object.assign({}, acc, { [value]: null }), {});
    }

    return recommendations.reduce((acc, value, index) => {
        if (value.error || (value.httpCode && isClientError(value.httpCode))) {
            acc[recommendationsToFetch[index]] = null;
        } else {
            const recommendationName = getRecommendationName(value);
            if (recommendationName && !acc[recommendationName]) {
                const products = getProductsFromResponse(recommendationName, value);
                acc[recommendationName] = getRecommendedProducts(
                    products,
                    availableImageSizes,
                    imageQuality,
                    maxRecommendations,
                    pdpUrlTemplate,
                    locale,
                    recommendationName,
                    imageTemplate,
                    imageViews,
                    outfitViewFirstFallback,
                    outfitViewSecondFallback,
                    imagesphereAspectRatio,
                );
            }
        }
        return acc;
    }, {});
};

const getRecommendationName = (value) => (Object.keys(value).length > 0 && Object.keys(value)[0] === 'ymal' ? YMAL : WIW);

const getProductsFromResponse = (recommendationName, value) => (recommendationName === YMAL ? value.ymal : value?.outfits?.[0]?.slots);

export const getRecommendedProducts = (
    recommendationData,
    availableImageSizes,
    imageQuality,
    maxRecommendations,
    pdpUrlTemplate,
    locale,
    recommendationName,
    imageTemplate,
    imageViews,
    outfitViewFirstFallback,
    outfitViewSecondFallback,
    imagesphereAspectRatio,
) => {
    return getRecommendedProductsFromStyling(
        recommendationData,
        availableImageSizes,
        imageQuality,
        maxRecommendations,
        pdpUrlTemplate,
        locale,
        recommendationName,
        imageTemplate,
        imageViews,
        outfitViewFirstFallback,
        outfitViewSecondFallback,
        imagesphereAspectRatio,
    );
};

const getRecommendedProductsFromStyling = (
    recommendationData = [],
    availableImageSizes,
    imageQuality,
    maxRecommendations,
    pdpUrlTemplate,
    locale,
    recommendationName,
    imageTemplate,
    imageViews,
    outfitViewFirstFallback,
    outfitViewSecondFallback,
    imagesphereAspectRatio,
) => {
    if (!recommendationData || recommendationData?.length === 0) {
        return null;
    }

    const products = recommendationData.reduce((acc, singleProduct) => {
        if (acc.length == maxRecommendations) return acc;

        const {
            designerName,
            designerIdentifier,
            masterCategory,
            name,
            shortDescription,
            badges = [],
            seo,
            partNumber,
            productId,
            visible,
            displayable,
        } = singleProduct;
        const description = shortDescription || name;
        const image = getImages(singleProduct, availableImageSizes, imageQuality, imagesphereAspectRatio);
        const price = getSelectedProductPrice(singleProduct);
        const href = getProductLinkByPartNumber(pdpUrlTemplate, locale, seo, partNumber);

        return [
            ...acc,
            {
                image,
                price,
                designer: designerName,
                designerIdentifier,
                description,
                partNumber,
                href,
                badge: badges[0],
                masterCategory,
                productId,
                visible,
                displayable,
            },
        ];
    }, []);

    if (recommendationName === 'WIW_ProductRec') {
        return [
            {
                products,
                outfitImage: getOutfitImage(
                    imageTemplate,
                    availableImageSizes,
                    imageQuality,
                    imageViews,
                    outfitViewFirstFallback,
                    outfitViewSecondFallback,
                    imagesphereAspectRatio,
                ),
            },
        ];
    }
    return products;
};

export const getOutfitImage = (
    imageTemplate,
    availableImageSizes,
    imageQuality,
    imageViews,
    outfitViewFirstFallback,
    outfitViewSecondFallback,
    imagesphereAspectRatio,
) => {
    const outfitView = imageViews.includes(outfitViewFirstFallback)
        ? outfitViewFirstFallback
        : imageViews.includes(outfitViewSecondFallback)
          ? outfitViewSecondFallback
          : imageViews[0];
    const { url, srcSet } = getProductImageFromTemplate(imageTemplate, outfitView, availableImageSizes, imageQuality, null, imagesphereAspectRatio);
    return {
        alt: '',
        url,
        srcSet,
    };
};

const getImages = (productData, availableImageSizes, imageQuality, imagesphereAspectRatio) => {
    const images = getSelectedProductImages(productData, productData, availableImageSizes, imageQuality, null, imagesphereAspectRatio);

    return {
        alt: images[0]?.alt,
        primary: images[0]?.url,
        primarySrcset: images[0]?.srcSet,
        secondary: images[1]?.url,
        secondarySrcset: images[1]?.srcSet,
    };
};
